import Button from '@mui/material/Button';
import Checkbox from '@mui/material/Checkbox';
import FormControlLabel from '@mui/material/FormControlLabel';
import Grid from '@mui/material/Grid';
import Link from '@mui/material/Link';
import Typography from '@mui/material/Typography';
import React, { ChangeEvent, useState, type JSX } from 'react';
import { UnterschriftenCheckboxState } from '../screens/Vorgang/Unterschriften';
import '../screens/Vorgang/Unterschriften.css';
import { Vorgang } from '../types';
import { SPACING_BETWEEN_FORM_FIELDS } from './common/spacings';
import { Modal } from './Modal';
import Divider from '@mui/material/Divider';
import { useDateiUrl } from '../hooks/useDateiUrl';
import { Signature } from './common/Signature';
import { Unterzeichner } from '../shared/constants';

type Props = {
  readonly vorgang: Vorgang;
  readonly isLoading: boolean;
  readonly updateCheckboxState: (newState: Partial<UnterschriftenCheckboxState>) => void;
  readonly checkboxState: UnterschriftenCheckboxState;
};

export function Widerrufsbelehrung({ vorgang, isLoading, updateCheckboxState, checkboxState }: Props): JSX.Element {
  const [isModalOpen, setIsModalOpen] = useState(false);

  return (
    <>
      <Grid size={12}>
        <FormControlLabel
          control={
            <Checkbox
              checked={checkboxState.widerrufsbelehrung}
              onChange={(event: ChangeEvent<HTMLInputElement>) => updateCheckboxState({ widerrufsbelehrung: event.target.checked })}
              name="widerrufsbelehrung"
              color="primary"
              data-testid="widerrufsbelehrungCheckbox"
            />
          }
          label={
            <>
              Ich habe die{' '}
              <Link
                onClick={(event) => {
                  event.preventDefault();
                  setIsModalOpen(true);
                }}
                data-testid="widerrufsbelehrungLink"
              >
                Widerrufsbelehrung
              </Link>{' '}
              zur Kenntnis genommen.*
            </>
          }
        />
        <div className="unterschriften__small-text-box">
          Ich verlange ausdrücklich, dass vor Ende der Widerrufsfrist mit der Ausführung der beauftragten Dienstleistung begonnen wird. Mir ist bewusst, dass
          ich bei vollständiger Auftragserfüllung durch den Unternehmer mein Widerrufsrecht verliere. Mir ist weiter bewusst, dass ich für den Fall, dass ich
          vor vollständiger Auftragserfüllung den Auftrag widerrufe, für die bis zum Widerruf bereits erbrachten Leistungen zu leisten habe.
        </div>
      </Grid>
      {isModalOpen && <WiderrufsbelehrungModal vorgang={vorgang} isLoading={isLoading} isModalOpen={isModalOpen} onClose={() => setIsModalOpen(false)} />}
    </>
  );
}

type ModalProps = {
  readonly vorgang: Vorgang;
  readonly isLoading: boolean;
  readonly isModalOpen: boolean;
  readonly onClose: () => void;
};

function WiderrufsbelehrungModal({ vorgang, isLoading, isModalOpen, onClose }: ModalProps): JSX.Element {
  const widerrufsbelehrungUrl = useDateiUrl(vorgang?.unterschriften?.widerrufsbelehrung?.key);

  const signDate = vorgang?.unterschriften?.widerrufsbelehrung?.datum ?? new Date().toISOString();

  return (
    <Modal
      openModal={isModalOpen}
      setOpenModal={onClose}
      title="Widerrufsbelehrung"
      body={
        <Grid container spacing={SPACING_BETWEEN_FORM_FIELDS} data-testid="widerrufsbelehrungModal">
          <Grid size={12}>
            <Typography variant="h6">Widerrufsbelehrung</Typography>
          </Grid>
          <Grid size={12}>
            <Typography variant="body2">
              Sie haben das Recht, binnen 14 Tagen ohne Angaben von Gründen diesen Vertrag zu widerrufen. Die Widerrufsfrist beträgt 14 Tage ab dem Tag des
              Gutachtenauftrags. <br />
              Um Ihr Widerrufsrecht auszuüben, müssen Sie
            </Typography>
          </Grid>
          <Grid size={12}>
            <Typography variant="body2">
              Experts24 Tassone GmbH <br />
              Hildener Str. 28 <br />
              42697 Solingen <br />
              Telefon: 0212-20005655 <br />
              E-Mail: service@experts24.de <br />
            </Typography>
          </Grid>
          <Grid size={12}>
            <Typography variant="body2">
              mittels einer eindeutigen Erklärung (z.B. ein mit der Post versandter Brief, Telefax oder E-Mail) über Ihren Entschluss, diesen Auftrag zu
              widerrufen, informieren.
            </Typography>
          </Grid>
          <Grid size={12}>
            <Typography variant="body2">
              Sie können dafür das unten angehängte Muster-Widerrufsformular verwenden, das jedoch nicht vorgeschrieben ist. Zur Wahrung der Widerrufsfrist
              reicht es aus, dass Sie die Mitteilung über die Ausübung des Widerrufsrechts vor Ablauf der Widerrufsfrist absenden.
            </Typography>
          </Grid>
          <Grid size={12}>
            <Typography variant="h6">Folgen des Widerrufs</Typography>
          </Grid>
          <Grid size={12}>
            <Typography variant="body2">
              Wenn Sie diesen Auftrag widerrufen, hat Experts24 Tassone GmbH Ihnen alle Zahlungen, die diese von Ihnen erhalten hat, einschließlich der
              Lieferkosten (mit Ausnahme der zusätzlichen Kosten, die sich daraus ergeben, dass Sie eine andere Art der Lieferung als die von uns angebotene,
              günstigste Standardlieferung gewählt haben), unverzüglich und spätestens binnen vierzehn Tagen ab dem Tag zurückzuzahlen, an dem die Mitteilung
              über Ihren Widerruf dieses Auftrages bei uns eingegangen ist. Für diese Rückzahlung verwenden wir dasselbe Zahlungsmittel, das Sie bei der
              ursprünglichen Transaktion eingesetzt haben, es sei denn, mit Ihnen wurde ausdrücklich etwas anderes vereinbart; in keinem Fall werden Ihnen wegen
              dieser Rückzahlung Entgelte berechnet. Wir können die Rückzahlung verweigern, bis wir die Ware wieder zurückerhalten haben oder bis Sie den
              Nachweis erbracht haben, dass Sie die Ware zurückgesandt haben, je nachdem, welches der frühere Zeitpunkt war.
            </Typography>
          </Grid>
          <Grid size={12}>
            <Typography variant="body2">
              Haben Sie verlangt, dass die Dienstleistungen während der Widerrufsfrist beginnen sollen, so haben Sie uns einen angemessenen Betrag zu zahlen,
              der dem Anteil der bis zu dem Zeitpunkt, zu dem Sie uns von der Ausübung des Widerrufsrechts hinsichtlich dieses Auftrages unterrichten, bereits
              erbrachten Dienstleistungen im Vergleich zum Gesamtumfang der im Auftrag vorhergesehenen Dienstleistungen entspricht. Ggf. werden wir eine
              Verrechnung von einer Ihnen geleisteten Zahlung mit unseren Kosten vornehmen.
            </Typography>
          </Grid>
          <Grid size={12}>
            <Divider />
          </Grid>
          <Grid size={12}>
            <Typography variant="h6">Widerrufsformular</Typography>
          </Grid>
          <Grid size={12}>
            <Typography variant="body2">
              Wenn Sie den Auftrag widerrufen wollen, dann füllen Sie bitte dieses Formular aus und senden Sie es an nachfolgende Adresse zurück:
            </Typography>
          </Grid>
          <Grid size={12}>
            <Typography variant="body2">
              Experts24 Tassone GmbH <br />
              Hildener Str. 28 <br />
              42697 Solingen <br />
              Telefon: 0212-20005655 <br />
              E-Mail: service@experts24.de <br />
            </Typography>
          </Grid>
          <Grid size={12}>
            <Typography variant="body2">
              Hiermit widerrufe(n) ich/wir () den von mir/uns abgeschlossenen Auftrag über die Erbringung der folgenden Dienstleistung:
            </Typography>
          </Grid>
          <Grid size={12}>
            <Typography variant="body2">Bestellt / Beauftragt am:</Typography>
          </Grid>
          <Grid size={12}>
            <Typography variant="body2">Name, Anschrift der/des Auftraggeber/s:</Typography>
          </Grid>
          <Grid size={12}>
            <Typography variant="body2">Datum:</Typography>
          </Grid>
          <Grid size={12}>
            <Typography variant="body2">Unterschrift der/des Auftraggeber/s:</Typography>
          </Grid>
          <Grid size={12}>
            <Typography variant="h6">Bestätigung</Typography>
          </Grid>
          <Grid size={12}>
            <Typography variant="body2">Ich habe die Widerrufsbelehrung zur Kenntnis genommen und ein Exemplar erhalten.</Typography>
          </Grid>
          <Grid size={12}>
            <Signature
              onSign={() => {}}
              onDeleteSignature={() => {}}
              signatureUrl={widerrufsbelehrungUrl}
              isLoading={isLoading}
              unterzeichnerAuswahl={vorgang?.unterschriften?.widerrufsbelehrung?.unterzeichner ?? Unterzeichner.AUFTRAGGEBER}
              vorgang={vorgang}
              date={signDate}
              readOnly={true}
            />
          </Grid>
        </Grid>
      }
      footer={
        <div className="unterschriften__modal-button">
          <Button color="primary" variant="contained" onClick={onClose}>
            Schließen
          </Button>
        </div>
      }
    />
  );
}
